import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import api from '../../api/api';
import { to } from '../../util/util';
import { history } from '../../index';
import { TopHoldersResult, TopHolderView as TopHolder } from '../../model/TopHoldersResult';

interface IProps {
    ticker: string;
    exchange: string;
}

const Wrapper = styled.div`
    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 1rem;
    }
    th, td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #e3e8ee;
        text-align: left;
    }
    th {
        background: #f6f9fc;
        color: #333;
        font-weight: 600;
    }
    td.num, th.num {
        text-align: right;
        white-space: nowrap;
    }
    tfoot td {
        font-weight: 600;
        background: #f6f9fc;
    }
    tbody tr.clickable {
        cursor: pointer;
    }
    tbody tr.clickable:hover td {
        background: #eef4fa;
    }
    tbody tr.member td {
        color: #555;
        font-size: 0.92em;
    }
    .top-holders__header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
    }
`;

const formatNumber = (value: string): string => {
    const n = Number(value);
    return isNaN(n) ? value : n.toLocaleString();
};

// Automic returns percentages as fractions of 1 (e.g. "0.153773" = 15.38%)
const formatPercent = (value: string): string => {
    const n = Number(value);
    return isNaN(n) ? value : (n * 100).toFixed(2);
};

const TopHolders: React.FC<IProps> = (props: IProps) => {

    const [result, setResult] = useState<TopHoldersResult | null>(null);
    const [error, setError] = useState('');

    useAsyncEffect(async (isMounted: () => boolean) => {
        const [err, data] = await to(api().getTopHolders(props.ticker, props.exchange));
        if (!isMounted()) return;
        if (err || !data.success) {
            setError(err?.message || data?.message || 'Failed to fetch top holders');
        } else {
            setResult(data);
        }
    }, []);

    // holderId is resolved server-side; rows without one are not clickable
    const openHolder = (holderId: number) => {
        // holder details live under the Search sub-tab (/client-stocks/:ticker/Share Registry/Search)
        const base = window.location.pathname.split('/').slice(0, 4).join('/');
        history.push(`${base}/Search?holderId=${holderId}`);
    };

    const holderName = (h: TopHolder) => h.group ? h.groupName : (h.holderNames || []).join(' & ');

    if (error) return <Wrapper><p style={{ color: '#c0392b' }}>{error}</p></Wrapper>;
    if (!result) return <Wrapper><p>Loading top holders from Automic&hellip;</p></Wrapper>;

    const report = result.report!;
    const holders = report.holders || [];

    return <Wrapper>
        <div className="top-holders__header">
            <h3>Top {holders.length} Holders {result.issuerName ? ` - ${result.issuerName}` : ''}</h3>
        </div>
        <table>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Holder</th>
                    <th>Holder No.</th>
                    <th className="num">Holding</th>
                    <th className="num">% Issued Capital</th>
                </tr>
            </thead>
            <tbody>
                {holders.map((h, i) =>
                    <React.Fragment key={i}>
                    <tr className={h.holderId ? 'clickable' : ''}
                        onClick={() => h.holderId && openHolder(h.holderId)}>
                        <td>{h.position}</td>
                        <td>{holderName(h)}</td>
                        <td>{h.holderNumber}</td>
                        <td className="num">{formatNumber(h.holding)}</td>
                        <td className="num">{formatPercent(h.issuedCapitalPercentage)}%</td>
                    </tr>
                    {h.group && (h.groupMembers || []).map((m, j) =>
                        <tr key={j} className={"member " + (m.holderId ? 'clickable' : '')}
                            onClick={() => m.holderId && openHolder(m.holderId)}>
                            <td></td>
                            <td style={{ paddingLeft: '2rem' }}>&#8627; {(m.holderNames || []).join(' & ')}</td>
                            <td>{m.holderNumber}</td>
                            <td className="num">{formatNumber(m.holding)}</td>
                            <td></td>
                        </tr>
                    )}
                    </React.Fragment>
                )}
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan={3}>Top {holders.length} holders</td>
                    <td className="num">{formatNumber(report.totalDisplayedIssuedCapital)}</td>
                    <td className="num">{formatPercent(report.totalDisplayedIssuedCapitalPercentage)}%</td>
                </tr>
                <tr>
                    <td colSpan={3}>Total issued capital</td>
                    <td className="num">{formatNumber(report.totalIssuedCapital)}</td>
                    <td className="num">{formatPercent(report.totalIssuedCapitalPercentage)}%</td>
                </tr>
            </tfoot>
        </table>
    </Wrapper>
}

export default TopHolders;
